// 121 — Psicología Clínica (Anexo 1 MSP, SNS-MSP/HCU-form.121/2021). Fase 7, Tanda 9: bloques B-K del papel (una
// consulta por documento). Campos y decisiones en `bloques121Datos.ts`. El bloque A lo resuelve `EncabezadoMspCampos`;
// el profesional (K) lo arma el servidor en el PDF firmado. Sin FHIR: `Par[]` en el PDF firmado.
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import { MARCAS_PRE_DEF, type CampoSimple, type Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoConMarca } from './MarcaDiagnostico';
import {
  CAMPOS_ANTECEDENTES_121,
  CAMPOS_DEMANDA_121,
  CAMPOS_EVALUACION_121,
  CAMPOS_FAMILIARES_121,
  CAMPOS_GENOGRAMA_121,
  CAMPOS_HABITOS_121,
  CAMPOS_INSTRUMENTOS_121,
  CAMPOS_MOTIVO_121,
  CAMPOS_PLAN_121,
  estadoInicial121,
  LEYENDA_GENOGRAMA_121,
  paresDe121,
  type Estado121,
} from './bloques121Datos';

const CODIGO = '121';
const sinAreas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo !== 'area');
const areas = (campos: readonly CampoSimple[]): readonly CampoSimple[] => campos.filter((c) => c.tipo === 'area');

export function Formulario121({ subject, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const { guardando, error, guardar } = useGuardado();
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const titulo = t('hub.formulario.121');
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, CODIGO, titulo);
  const [estado, setEstado] = useState<Estado121>(() => estadoInicial121());

  type ClaveValores = 'motivo' | 'demanda' | 'antecedentes' | 'habitos' | 'familiares' | 'genograma' | 'evaluacion' | 'instrumentos' | 'plan';
  /** Casillas/selects (fila de campos simples) + sus áreas de texto con dictado, para un bloque de `Estado121`. */
  const bloque = (clave: ClaveValores, prefijo: string, lista: readonly CampoSimple[], columnas: 2 | 3 | 4, filas = 3): JSX.Element => {
    const valores: Valores = estado[clave];
    const poner = (v: Valores): void => setEstado({ ...estado, [clave]: v });
    const simples = sinAreas(lista);
    return (
      <>
        {simples.length > 0 && <CamposSimples prefijoId={`f121-${prefijo}`} campos={simples} valores={valores} columnas={columnas} onChange={(v) => poner({ ...valores, ...v })} />}
        {areas(lista).map((c) => (
          <AreaDictado key={c.clave} id={`f121-${prefijo}-${c.clave}`} etiqueta={c.etiqueta} valor={valores[c.clave] ?? ''} filas={filas} onChange={(v) => poner({ ...valores, [c.clave]: v })} />
        ))}
      </>
    );
  };

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      onGuardado(paresDe121(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f121.motivoTitulo')}>{bloque('motivo', 'mot', CAMPOS_MOTIVO_121, 2)}</Bloque>
      <Bloque titulo={t('formularios.f121.demandaTitulo')}>{bloque('demanda', 'dem', CAMPOS_DEMANDA_121, 2, 5)}</Bloque>
      <Bloque titulo={t('formularios.f121.antecedentesTitulo')}>{bloque('antecedentes', 'ant', CAMPOS_ANTECEDENTES_121, 4)}</Bloque>
      <Bloque titulo={t('formularios.f121.habitosTitulo')}>{bloque('habitos', 'hab', CAMPOS_HABITOS_121, 4)}</Bloque>
      <Bloque titulo={t('formularios.f121.familiaresTitulo')}>{bloque('familiares', 'fam', CAMPOS_FAMILIARES_121, 2)}</Bloque>

      <Bloque titulo={t('formularios.f121.genogramaTitulo')}>
        {bloque('genograma', 'gen', CAMPOS_GENOGRAMA_121, 2, 5)}
        <div className="rounded-md border border-border p-3 text-xs text-hce-muted">
          <p>
            <span className="font-medium">{t('formularios.f121.simbologia')}:</span> {LEYENDA_GENOGRAMA_121.simbologia.join(' · ')}
          </p>
          <p>
            <span className="font-medium">{t('formularios.f121.relaciones')}:</span> {LEYENDA_GENOGRAMA_121.relaciones.join(' · ')}
          </p>
          <p>
            <span className="font-medium">{t('formularios.f121.abreviaturas')}:</span> {LEYENDA_GENOGRAMA_121.abreviaturas.join(' · ')}
          </p>
        </div>
      </Bloque>

      <Bloque titulo={t('formularios.f121.evaluacionTitulo')}>
        {bloque('evaluacion', 'eva', CAMPOS_EVALUACION_121, 2, 6)}
        {bloque('instrumentos', 'ins', CAMPOS_INSTRUMENTOS_121, 2, 3)}
      </Bloque>

      <Bloque titulo={t('formularios.f121.diagnosticoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f121.diagnosticoNota')}</p>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoConMarca
            key={i}
            label={`${t('formularios.f121.diagnostico')} ${i + 1}`}
            marcaLabel={t('formularios.f121.marcaDiagnostico')}
            marcas={MARCAS_PRE_DEF}
            value={d}
            onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })}
          />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f121.planTitulo')}>{bloque('plan', 'plan', CAMPOS_PLAN_121, 2, 8)}</Bloque>

      <Bloque titulo={t('formularios.f121.profesionalTitulo')}>
        <p className="text-sm text-hce-muted">{t('formularios.profesionalNotaCedula')}</p>
      </Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
